import * as React from "react";

export interface ContainerSize {
  width: number;
  height: number;
}

/**
 * Observes an element's content-box size via ResizeObserver.
 * Returns { width, height } that update whenever the element resizes.
 *
 * Accepts the element directly (not a ref) so the effect re-runs whenever the
 * element mounts or unmounts — critical for conditionally-rendered elements.
 * Use a useState callback ref at the call site: `ref={setEl}`.
 *
 * Pass `initial` to seed the measurement before the first observation fires —
 * useful when the first render should assume a specific size to avoid a layout flash.
 */
export function useContainerSize(
  el: Element | null,
  initial: ContainerSize = { width: 0, height: 0 },
): ContainerSize {
  const [size, setSize] = React.useState<ContainerSize>(initial);

  React.useEffect(() => {
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const box = entry.contentBoxSize?.[0];
      setSize(
        box
          ? { width: box.inlineSize, height: box.blockSize }
          : { width: entry.contentRect.width, height: entry.contentRect.height },
      );
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);

  return size;
}
